<template>
    <div ref="container" class="image-interaction" @click.stop="handleContainerClick">
        <click-action v-if="shouldWrapClickAction" :type="type" :data="image.clickAction">
            <slot></slot>
        </click-action>
        <slot v-else></slot>
        <div v-if="showHotspots" class="hotspot-layer">
            <click-action
                v-for="hotspot in image.hotspots"
                :key="hotspot.id"
                class="hotspot-link"
                :style="getHotspotStyle(hotspot)"
                :type="type"
                :data="hotspot.clickAction">
                <span class="hotspot-name">{{ hotspot.name }}</span>
            </click-action>
        </div>
    </div>
</template>

<script>
    import ClickAction from "../common/ClickAction"

    export default {
        name: "ImageInteraction",
        components: {
            ClickAction
        },
        props: {
            image: {
                type: Object,
                default: () => ({})
            },
            type: String,
            imageFit: {
                type: String,
                default: "fill"
            },
            legacyPreview: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                naturalWidth: 0,
                naturalHeight: 0,
                containerWidth: 0,
                containerHeight: 0
            }
        },
        computed: {
            interactionMode() {
                return this.image && this.image.interactionMode
            },
            isLegacy() {
                return !this.interactionMode
            },
            shouldWrapClickAction() {
                return this.isLegacy || this.interactionMode === "action"
            },
            showHotspots() {
                return this.interactionMode === "hotspots" && Array.isArray(this.image.hotspots)
            }
        },
        watch: {
            "image.imageSrc"() {
                this.loadNaturalImage()
            },
            imageFit() {
                this.refreshLayout()
            }
        },
        mounted() {
            this.loadNaturalImage()
            this.refreshLayout()
            window.addEventListener("resize", this.refreshLayout)
        },
        beforeDestroy() {
            window.removeEventListener("resize", this.refreshLayout)
        },
        methods: {
            handleContainerClick() {
                if (
                    this.interactionMode === "preview" ||
                    (this.isLegacy && this.legacyPreview && this.image.disablePreview !== true)
                ) {
                    this.$emit("preview")
                }
            },
            loadNaturalImage() {
                const src = this.image && this.image.imageSrc
                if (!src) {
                    this.naturalWidth = 0
                    this.naturalHeight = 0
                    return
                }
                const image = new Image()
                image.onload = () => {
                    this.naturalWidth = image.naturalWidth
                    this.naturalHeight = image.naturalHeight
                    this.refreshLayout()
                }
                image.src = src
            },
            refreshLayout() {
                this.$nextTick(() => {
                    const rect = this.$refs.container && this.$refs.container.getBoundingClientRect()
                    this.containerWidth = rect ? rect.width : 0
                    this.containerHeight = rect ? rect.height : 0
                })
            },
            getRenderedImageLayout() {
                const containerWidth = this.containerWidth
                const containerHeight = this.containerHeight
                const naturalWidth = this.naturalWidth
                const naturalHeight = this.naturalHeight

                if (!containerWidth || !containerHeight || !naturalWidth || !naturalHeight) {
                    return { width: containerWidth, height: containerHeight, left: 0, top: 0 }
                }

                if (this.imageFit === "fill") {
                    return { width: containerWidth, height: containerHeight, left: 0, top: 0 }
                }

                let scale
                if (this.imageFit === "cover") {
                    scale = Math.max(containerWidth / naturalWidth, containerHeight / naturalHeight)
                } else if (this.imageFit === "none") {
                    scale = 1
                } else if (this.imageFit === "scale-down") {
                    scale = Math.min(1, Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight))
                } else {
                    scale = Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight)
                }

                const width = naturalWidth * scale
                const height = naturalHeight * scale
                return {
                    width,
                    height,
                    left: (containerWidth - width) / 2,
                    top: (containerHeight - height) / 2
                }
            },
            getHotspotStyle(hotspot) {
                const layout = this.getRenderedImageLayout()
                if (!this.containerWidth || !this.containerHeight) {
                    return {}
                }

                return {
                    left: `${((layout.left + (layout.width * hotspot.x) / 100) / this.containerWidth) * 100}%`,
                    top: `${((layout.top + (layout.height * hotspot.y) / 100) / this.containerHeight) * 100}%`,
                    width: `${((layout.width * hotspot.width) / 100 / this.containerWidth) * 100}%`,
                    height: `${((layout.height * hotspot.height) / 100 / this.containerHeight) * 100}%`
                }
            }
        }
    }
</script>

<style lang="less" scoped>
    .image-interaction {
        position: relative;
        display: block;
        width: 100%;
        height: 100%;
    }

    .hotspot-layer {
        position: absolute;
        inset: 0;
        overflow: hidden;
        z-index: 2;
    }

    .hotspot-link {
        position: absolute;
        display: block;
        min-width: 1px;
        min-height: 1px;
    }

    .hotspot-name {
        position: absolute;
        top: 0;
        left: 0;
        width: 1px;
        height: 1px;
        overflow: hidden;
    }
</style>
